import { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import { getContrastRatio } from '../Components/Colorize/getTextColor';
import chroma from 'chroma-js';
import '../Styles/savedAndThemes.scss';
import Header from '../Components/ReusableComponents/Header';
import themesData from '../Assets/JSON/themes.json';
import { usePopupQueue } from '../Components/ReusableComponents/usePopupQueue';
import { useMembershipGuard } from '../Components/Auth/useMembershipGuard';

function Themes() {
  const [themes, setThemes] = useState([]);
  const [displayMode, setDisplayMode] = useState('mono');
  const { activePopups, addPopup } = usePopupQueue();

  const { executeMembershipGuard } = useMembershipGuard();

  // Load themes from the JSON file
  useEffect(() => {
    setThemes(themesData.themes);
  }, []);

  // Helper function to determine how many palettes to show for each theme
  const getPalettesToShow = (theme) => {
    switch (displayMode) {
      case 'mono':
        return theme.palettes.slice(0, 1);
      case 'duo':
        return theme.palettes.slice(0, 2);
      case 'trio':
        return theme.palettes.slice(0, 3);
      default:
        return theme.palettes.slice(0, 1);
    }
  };


  const copyToClipboard = async (colorCode) => {
    try {
      // Ensure the color code includes the # if it's a hex color
      const formattedColor = colorCode.startsWith('#') ? colorCode : `#${colorCode}`;

      await navigator.clipboard.writeText(formattedColor);
      addPopup(
        <span className="copied">
          Copied "{formattedColor}"
        </span>,
        4000,
        'copy'
      );

    } catch (err) {
      console.error('Failed to copy color to clipboard:', err);

      // Fallback for older browsers - define formattedColor here too
      const formattedColor = colorCode.startsWith('#') ? colorCode : `#${colorCode}`;
      const textArea = document.createElement('textarea');
      textArea.value = formattedColor;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="themes">
      <Header
        // titleHighlighted="Find inspiration in"
        headingRegular="Find inspiration in our curated themes"
        bodyText="Browse through our collection of carefully curated color themes to find the perfect palette for your next project."
      >
        <div className="themes-mode--container">
          <button
            className={`themes-mode-btn ${displayMode === 'mono' ? 'active' : ''}`}
            onClick={() => setDisplayMode('mono')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="2" viewBox="0 0 16 2" fill="none">
              <path d="M1 1H15" stroke="#8c8c8c" strokeLinecap="round" />
            </svg>
            <span>Mono scale</span>
          </button>

          <button
            className={`themes-mode-btn ${displayMode === 'duo' ? 'active' : ''}`}
            onClick={() => executeMembershipGuard(() => setDisplayMode('duo'))}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="6" viewBox="0 0 16 6" fill="none">
              <path d="M1 1H15" stroke="#8c8c8c" strokeLinecap="round" />
              <path d="M1 5H15" stroke="#8c8c8c" strokeLinecap="round" />
            </svg>
            <span>Duo palette</span>
          </button>

          <button
            className={`themes-mode-btn ${displayMode === 'trio' ? 'active' : ''}`}
            onClick={() => executeMembershipGuard(() => setDisplayMode('trio'))}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="10" viewBox="0 0 16 10" fill="none">
              <path d="M1 1H15" stroke="#8c8c8c" strokeLinecap="round" />
              <path d="M1 5H15" stroke="#8c8c8c" strokeLinecap="round" />
              <path d="M1 9H15" stroke="#8c8c8c" strokeLinecap="round" />
            </svg>
            <span>Trio palette</span>
          </button>
          
        </div>

      </Header>

      <div className="scales--container">
        {themes.map((theme, themeIndex) => (
          <div className="scales--card" key={themeIndex}>
            <div className="scales--header">
              <div className="scales--theme-content">
                <div className='svg---text'>
                  <svg width={26} height={26} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#211f1c">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5" />
                  </svg>
                  <h3>{theme.name}</h3>
                </div>
                
                <p>{theme.description}</p>
              </div>
              <div className="scales--actions">
                <Link
                  to={theme.urls?.[displayMode] || theme.urls?.mono || '#'}
                  target="_blank"
                  className="action view-btn svg---text"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 22 22" fill="none">
                    <path d="M1.8659 11.2955C1.80257 11.1055 1.80251 10.8998 1.86573 10.7098C3.13839 6.88391 6.74737 4.125 11.0007 4.125C15.2521 4.125 18.8597 6.88135 20.1339 10.7045C20.1973 10.8945 20.1973 11.1002 20.1341 11.2902C18.8614 15.1161 15.2525 17.875 10.9991 17.875C6.74774 17.875 3.14014 15.1187 1.8659 11.2955Z" stroke="#211f1c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M13.75 11C13.75 12.5188 12.5188 13.75 11 13.75C9.48119 13.75 8.24998 12.5188 8.24998 11C8.24998 9.48122 9.48119 8.25 11 8.25C12.5188 8.25 13.75 9.48122 13.75 11Z" stroke="#211f1c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>View</span>
                </Link>
              </div>
            </div>

            <div className="scales--content">
              {getPalettesToShow(theme).map((paletteData, paletteIndex) => (
                <div className="scale--content" key={paletteIndex}>
                  <h4 className="color-name">{paletteData.colorName}</h4>
                  <div className="squares">
                    <div className="squares--container">
                      {paletteData.palette.map((shade, idx) => (
                        <div
                          key={idx}
                          className={`square-container ${paletteData.selectedIndex === idx ? 'picked-color' : ''}`}
                        >
                          <div
                            className="square"
                            style={{
                              backgroundColor: shade,
                              color: getContrastRatio(shade, '#ffffff') > 2.0
                                ? '#ffffff' : '#211f1c'
                            }}
                            onClick={() => copyToClipboard(shade.startsWith('hsl') ? chroma(shade).hex() : shade)}
                          >
                            {paletteData.selectedIndex === idx && (
                              <svg className="flag-icon" xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 17 17" fill="none">
                                <ellipse cx="5.66667" cy="15.2292" rx="5.66667" ry="1.77083" fill="currentColor" />
                                <path d="M16.2923 1.41667H6.37565V0H4.95898V14.1667H6.37565V8.5H16.2923L14.8757 4.95833L16.2923 1.41667ZM12.0423 4.95833C12.0423 5.7375 11.4048 6.375 10.6257 6.375C9.84648 6.375 9.20898 5.7375 9.20898 4.95833C9.20898 4.17917 9.84648 3.54167 10.6257 3.54167C11.4048 3.54167 12.0423 4.17917 12.0423 4.95833Z" fill="currentColor" />
                              </svg>
                            )}
                            <p className="variable">{paletteData.squareNames[idx]}</p>
                            <p className="color-code">
                              {(shade.startsWith('hsl') ? chroma(shade).hex() : shade).replace('#', '')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* NEW SINGLE POPUP */}
      {activePopups.length > 0 && (
        <div className="popup-timed-container centered">
          {activePopups.map((popup) => (
            <div
              key={popup.id}
              className={`popup-timed ${popup.type || 'default'}`}
              style={{ minHeight: 'auto' }} // Reset dimensions
            >
              <div className="popup-timed-content">
                <div className="content">
                  {popup.type === 'error' ? (
                    // Error/warning icon
                    <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#b56600">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                    </svg>

                  ) : (
                    // Success/checkmark icon (default)
                    // <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    //   <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    // </svg>
                      <svg className="check-icon-fill" width={24} height={24} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                      </svg>
                  )}
                  {popup.message}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Themes;